import React, { useEffect } from 'react';
import css from './SignupForm.module.css';
import { required } from '../../../util/validators';
import DateOfBirthField from './DateOfBirthField';
import { FieldArray } from 'react-final-form-arrays';
import { Field } from 'react-final-form';
import { Icons, MediaUpload, SecondaryButton } from '../../../components';
import { useIntl } from 'react-intl';
import { FieldTypes } from '../../../util/enums';
import classNames from 'classnames';
import { socialMediaOptions } from '../../../config/configExtras';
import { isArrayLength } from '../../../util/genericHelpers';
import RenderTalentCustomLinks from './RenderCustomLinks';

const { INTEREST, LIFE_EVENT, SOCIAL_MEDIA, AFFILIATION } = FieldTypes;

const InputField = ({ name, label, placeholder, component = 'input' }) => (
  <div className={css.formFld}>
    <label>{label}</label>
    <Field name={name} component={component} placeholder={placeholder} />
  </div>
);

const defaultSocialMediaFields = [
  { type: SOCIAL_MEDIA, platform: 'tiktok', handle: '', followers: '' },
  { type: SOCIAL_MEDIA, platform: 'twitter', handle: '', followers: '' },
  { type: SOCIAL_MEDIA, platform: 'instagram', handle: '', followers: '' },
];

const TalentFormCard = ({
  values,
  push,
  config,
  form,
  renderSocialMediaFields,
  viewport,
  isSignupPage,
}) => {
  const { userType } = values || {};
  const intl = useIntl();
  const listingImageConfig = config.layout.listingImage;
  const isMobileLayout = viewport.width && viewport.width > 0 && viewport.width < 768;

  useEffect(() => {
    if (!values.profileDetails || values.profileDetails.length === 0) {
      defaultSocialMediaFields.forEach(field => push('profileDetails', field));
    }
  }, [push, values.profileDetails]);

  const getFieldLabel = (platform, field) => {
    return intl.formatMessage({ id: `TalentFormCard.${platform}.${field}.label` });
  };

  const getFieldPlaceholder = (platform, field) => {
    return intl.formatMessage({ id: `TalentFormCard.${platform}.${field}.placeholder` });
  };

  const usedPlatforms = isArrayLength(values?.profileDetails)
    ? values?.profileDetails.map(field => field?.name).filter(Boolean)
    : null;

  return (
    <div className={css.talentFormCard}>
      {/* Social Media & Life Event Fields */}
      <FieldArray name="profileDetails">
        {({ fields }) => (
          <div
            className={classNames(isSignupPage ? css.signupCustomFields : null, css.customFields)}
          >
            {/* Social Media Section */}
            <div className={css.section}>
              <label className={css.sectionTitle}>
                {intl.formatMessage({ id: 'TalentFormCard.socialMedia.title' })}
              </label>
              {fields.map((name, index) => {
                const fieldType = fields.value[index].type;
                const platform = fields.value[index].platform || '';

                if (fieldType === SOCIAL_MEDIA) {
                  return (
                    <div key={name} className={classNames(css.customField, css.addFields)}>
                      {index > 2 && (
                        <div className={css.formFld}>
                          <label htmlFor={`${name}.name`}>{getFieldLabel(platform, 'type')}</label>
                          <Field
                            name={`${name}.name`}
                            component="select"
                            id={`${name}.name`}
                            className={css.selectField}
                          >
                            <option value="" disabled>
                              {getFieldPlaceholder(platform, 'type')}
                            </option>
                            {socialMediaOptions.map(option => (
                              <option
                                key={option.key}
                                value={option.key}
                                disabled={usedPlatforms.includes(option.key)}
                              >
                                {option.label}
                              </option>
                            ))}
                          </Field>
                        </div>
                      )}
                      <div className={css.formFld}>
                        <InputField
                          name={`${name}.handle`}
                          label={getFieldLabel(platform, 'handle')}
                          placeholder={getFieldPlaceholder(platform, 'handle')}
                        />
                      </div>
                      <div className={css.formFld}>
                        <InputField
                          name={`${name}.followers`}
                          label={getFieldLabel(platform, 'followers')}
                          placeholder={getFieldPlaceholder(platform, 'followers')}
                        />
                      </div>
                      {index > 2 && (
                        <button
                          className={css.delete}
                          type="button"
                          onClick={() => fields.remove(index)}
                        >
                          {isMobileLayout ? (
                            <>
                              <Icons name="bin" /> Delete
                            </>
                          ) : (
                            <Icons name="bin" />
                          )}
                        </button>
                      )}
                    </div>
                  );
                }
                return null;
              })}
              <SecondaryButton
                className={css.addBtn}
                type="button"
                onClick={() =>
                  fields.push({ type: SOCIAL_MEDIA, platform: 'custom', handle: '', followers: '' })
                }
              >
                {intl.formatMessage({ id: 'TalentFormCard.addSocialMedia' })}
              </SecondaryButton>
            </div>

            {/* Significant Life Event Section */}
            <div className={css.section}>
              <label className={css.sectionTitle}>
                {intl.formatMessage({ id: 'TalentFormCard.lifeEvent.title' })}
              </label>
              {fields.map((name, index) => {
                const fieldType = fields.value[index].type;
                if (fieldType === LIFE_EVENT) {
                  return (
                    <div key={name} className={classNames(css.customField, css.addFields)}>
                      <InputField
                        name={`${name}.value`}
                        label={intl.formatMessage({ id: 'TalentFormCard.lifeEvent.label' })}
                        placeholder={intl.formatMessage({
                          id: 'TalentFormCard.lifeEvent.placeholder',
                        })}
                        component="textarea"
                      />
                      {index > 2 && (
                        <button
                          className={css.delete}
                          type="button"
                          onClick={() => fields.remove(index)}
                        >
                          {isMobileLayout ? (
                            <>
                              <Icons name="bin" /> Delete
                            </>
                          ) : (
                            <Icons name="bin" />
                          )}
                        </button>
                      )}
                    </div>
                  );
                }
                return null;
              })}
              <SecondaryButton
                className={css.addBtn}
                type="button"
                onClick={() => fields.push({ type: LIFE_EVENT })}
              >
                {intl.formatMessage({ id: 'TalentFormCard.addLifeEvent' })}
              </SecondaryButton>
            </div>
          </div>
        )}
      </FieldArray>

      {/* Affiliations Section as a Separate Entity */}

      <div>
        <FieldArray name="affiliations">
          {({ fields }) => (
            <div className={css.section}>
              <label className={css.sectionTitle}>
                {intl.formatMessage({ id: 'TalentFormCard.affiliations.title' })}
              </label>
              <p className={css.affiliationsDescription}>
                {' '}
                {intl.formatMessage({ id: 'TalentFormCard.affiliationsDescription' })}
              </p>
              {fields.map((name, index) => (
                <div
                  key={name}
                  className={classNames(
                    css.customField,
                    css.addCustomField,
                    css.addCustomAffiliationsField
                  )}
                >
                  <div className={css.textField}>
                    <InputField
                      name={`${name}.organization`}
                      label={intl.formatMessage({
                        id: 'TalentFormCard.affiliations.organization.label',
                      })}
                      placeholder={intl.formatMessage({
                        id: 'TalentFormCard.affiliations.organization.placeholder',
                      })}
                      className={css.textInputField}
                    />
                  </div>
                  <div className={css.textField}>
                    <InputField
                      name={`${name}.role`}
                      label={intl.formatMessage({ id: 'TalentFormCard.affiliations.role.label' })}
                      placeholder={intl.formatMessage({
                        id: 'TalentFormCard.affiliations.role.placeholder',
                      })}
                      className={css.textInputField}
                    />
                  </div>

                  <button className={css.delete} type="button" onClick={() => fields.remove(index)}>
                    {isMobileLayout ? (
                      <>
                        <Icons name="bin" /> Delete
                      </>
                    ) : (
                      <Icons name="bin" />
                    )}
                  </button>
                </div>
              ))}
              <SecondaryButton className={css.addBtn} type="button" onClick={() => fields.push({})}>
                {intl.formatMessage({ id: 'TalentFormCard.addAffiliation' })}
              </SecondaryButton>
            </div>
          )}
        </FieldArray>
      </div>
      <div className={css.uploadSection}>
        <MediaUpload
          form={form}
          fieldName="pictures"
          acceptedTypes={{
            'image/jpeg': [],
            'image/png': [],
          }}
          multiple={true}
          listingImageConfig={listingImageConfig}
          heading={intl.formatMessage({ id: 'TalentFormCard.pictureUploadHeading' })}
          description={intl.formatMessage({ id: 'TalentFormCard.pictureUploadDescription' })}
          userType={userType}
          values={values}
        />
      </div>
    </div>
  );
};

export default TalentFormCard;
